// Data/ListRow (Figma 20:51) — 64px tall, 72px pitch (64 + an 8px gap),
// 12px padding, a border-bottom on every row including the last.
//
// It renders <li> inside <ol>, not a table row: the Figma component doc
// says outright that "the documents and confusion lists are <ol> in code,
// not <table>". They are ranked lists of one thing, not grids of many
// columns, and a screen reader should hear them that way.

import "./ui.css";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export type ListRowProps = {
  title: ReactNode;
  /** The secondary line, e.g. "api-reference · 412 hovers · 63 quick exits". */
  meta?: ReactNode;
  /** Right-hand slot — a Badge carrying the score, then a chevron. */
  trailing?: ReactNode;
  /**
   * In-app destination. Renders a router `<Link>`, so the whole 64px row is the
   * target AND the navigation is a client one — a plain `<a href>` here reloads
   * the document, which remounts the shell and makes the browser-back beat
   * (US-152) unobservable. Needs a router in context; tests use
   * `src/testing/routed.tsx`.
   */
  to?: string;
  /** Query params for `to`, e.g. the document a doc-local term id belongs to. */
  search?: Record<string, unknown>;
  /** An external or non-routed destination. Plain anchor, full page load. */
  href?: string;
};

export function ListRow({
  title,
  meta,
  trailing,
  to,
  search,
  href,
}: ListRowProps) {
  const body = (
    <>
      <span className="maisight-ui-list-row-main">
        <span className="maisight-ui-list-row-title type-numSm">{title}</span>
        {meta ? (
          <span className="maisight-ui-list-row-meta type-bodyS">{meta}</span>
        ) : null}
      </span>
      {trailing ? (
        <span className="maisight-ui-list-row-trailing">{trailing}</span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <li>
        <Link className="maisight-ui-list-row" to={to} search={search}>
          {body}
        </Link>
      </li>
    );
  }

  return (
    <li>
      {href ? (
        <a className="maisight-ui-list-row" href={href}>
          {body}
        </a>
      ) : (
        <div className="maisight-ui-list-row">{body}</div>
      )}
    </li>
  );
}

export type ListProps = {
  /** Names the list for assistive tech — an <ol> of rows with no name is a
   *  pile of links. */
  label: string;
  children: ReactNode;
};

export function List({ label, children }: ListProps) {
  return (
    <ol className="maisight-ui-list" aria-label={label}>
      {children}
    </ol>
  );
}
